import React from 'react';
import { SetPlacar } from '../../types/placar';
import { ValidacaoSetPlacarResult, BeachTennisScoreValidator } from '../../utils/beachTennisScoreValidator';


interface ScoreboardGridProps {
  sets: SetPlacar[];
  numeroSets?: number;
  gamesPorSets?: number | null;
  tiebreaks?: number | null;
  categoriaTorneio?: {
    numeroSets?: number | null;
    gamesPorSets?: number | null;
    tiebreaks?: number | null;
  } | null;
  canEdit?: boolean;
  onSetFieldUpdate: (
    numeroSet: number,
    campo: 'gamesA' | 'gamesB' | 'tiebreakA' | 'tiebreakB',
    value: number | null
  ) => void;
  camposInvalidos?: ValidacaoSetPlacarResult[];
}

/**
 * Componente para exibir o grid de placar de beach tennis
 * 
 * Exemplo de uso:
 * <ScoreboardGrid
 *  sets={sets}
 *  numeroSets={3}
 *  canEdit={true}
 *  onSetFieldUpdate={(numeroSet, campo, value) => {
 *    console.log(`Set ${numeroSet}, campo ${campo}: ${value}`);
 *  }}
 *  camposInvalidos={[]}
 * />
 * @param sets - Array de sets
 * @param numeroSets - Número de sets
 * @param canEdit - Se o grid pode ser editado
 * @param onSetFieldUpdate - Função para atualizar o campo de um set
 * @param camposInvalidos - Array de campos inválidos
 * @returns 
 */
const ScoreboardGrid: React.FC<ScoreboardGridProps> = ({
  sets,
  numeroSets,
  gamesPorSets,
  tiebreaks,
  categoriaTorneio,
  canEdit = false,
  onSetFieldUpdate,
  camposInvalidos = [],
}) => {
  // Função para verificar se um campo está inválido
  const isCampoInvalido = (numeroSet: number, campo: 'gamesA' | 'gamesB' | 'tiebreakA' | 'tiebreakB'): boolean => {
    const campoInvalido = camposInvalidos.find(c => c.numeroSet === numeroSet);
    return campoInvalido ? campoInvalido[campo] : false;
  };

  // Função para verificar se um set deve ter tiebreak preenchido
  const deveTerTiebreak = (set: SetPlacar): boolean => {
    // Prioridade: partida > categoria
    const finalGamesPorSets = gamesPorSets ?? categoriaTorneio?.gamesPorSets ?? null;
    const finalTiebreaks = tiebreaks ?? categoriaTorneio?.tiebreaks ?? null;
    
    return BeachTennisScoreValidator.deveTerTiebreak(set, finalGamesPorSets, finalTiebreaks);
  };

  // Função para determinar se um set específico é o Super Tiebreak
  const isSuperTiebreak = (numeroSet: number): boolean => {
    // Prioridade: partida > categoria (mesma lógica usada em getConfiguracaoTexto)
    const finalNumeroSets = numeroSets ?? categoriaTorneio?.numeroSets ?? null;
    
    // Verifica se numeroSets existe, é par e maior que zero
    if (!finalNumeroSets || finalNumeroSets <= 0 || finalNumeroSets % 2 !== 0) {
      return false;
    }
    
    // Quando numeroSets é par, o normalizeSets adiciona 1 set adicional
    // O último set terá numeroSet === numeroSets + 1
    return numeroSet === finalNumeroSets + 1;
  };

  // Função para gerar o texto descritivo da configuração
  const getConfiguracaoTexto = (): string => {
    // Prioridade: partida > categoria
    const finalNumeroSets = numeroSets ?? categoriaTorneio?.numeroSets ?? null;
    const finalGamesPorSets = gamesPorSets ?? categoriaTorneio?.gamesPorSets ?? null;
    const finalTiebreaks = tiebreaks ?? categoriaTorneio?.tiebreaks ?? null;

    const partes: string[] = [];
    const camposFaltando: string[] = [];

    if (finalNumeroSets !== null && finalNumeroSets > 0) {
      partes.push(`${finalNumeroSets} sets`);
    } else {
      camposFaltando.push('número de sets');
    }

    if (finalGamesPorSets !== null && finalGamesPorSets > 0) {
      partes.push(`${finalGamesPorSets} games`);
    } else {
      camposFaltando.push('games por set');
    }

    if (finalTiebreaks !== null && finalTiebreaks > 0) {
      partes.push(`tiebreak no ${finalTiebreaks} a ${finalTiebreaks}`);
    } else {
      camposFaltando.push('tiebreak');
    }

    if (camposFaltando.length > 0) {
      const textoCompleto = partes.length > 0 
        ? `${partes.join(' com ')} - Configuração incompleta: ${camposFaltando.join(', ')}`
        : `Configuração incompleta: ${camposFaltando.join(', ')}`;
      return textoCompleto;
    }

    return partes.join(' com ');
  };
  // Garantir que temos o número correto de sets
  // Prioridade: partida > categoria (mesma lógica usada em outras funções)
  const finalNumeroSets = numeroSets ?? categoriaTorneio?.numeroSets ?? 0;
  const displaySets = sets.length > 0 ? sets : Array.from({ length: finalNumeroSets }, (_, i) => ({
    numeroSet: i + 1,
    gamesA: null,
    gamesB: null,
    tiebreakA: null,
    tiebreakB: null,
  }));

  return (
    <div className="mb-0 d-flex flex-column align-items-center">
      <h6 className="mb-3 fw-bold">Placar Completo</h6>
      <p className="mb-3 text-muted small" style={{ fontStyle: 'italic' }}>
        {getConfiguracaoTexto()}
      </p>
      <div className="border rounded overflow-hidden" style={{ width: 'fit-content' }}>
        {/* Cabeçalho */}
        <div
          className="d-flex align-items-center"
          style={{
            backgroundColor: '#f8f9fa',
            borderBottom: '2px solid #dee2e6',
            minHeight: '40px',
          }}
        >
          <div
            className="fw-bold d-flex align-items-center justify-content-center"
            style={{ minWidth: '50px', padding: '8px', borderRight: '1px solid #dee2e6' }}
          >
            Set
          </div>
          <div
            className="fw-bold d-flex align-items-center justify-content-center"
            style={{ flex: '1 1 0', padding: '8px', borderRight: '1px solid #dee2e6' }}
          >
            Games A
          </div>
          <div
            className="fw-bold d-flex align-items-center justify-content-center"
            style={{ flex: '1 1 0', padding: '8px' }}
          >
            TB A
          </div>
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: '50px',
              padding: '8px',
              borderLeft: '2px solid #000',
              borderRight: '2px solid #000',
              backgroundColor: '#e9ecef',
            }}
          >
            <span className="fw-bold">x</span>
          </div>
          <div
            className="fw-bold d-flex align-items-center justify-content-center"
            style={{ flex: '1 1 0', padding: '8px', borderRight: '1px solid #dee2e6' }}
          >
            Games B
          </div>
          <div
            className="fw-bold d-flex align-items-center justify-content-center"
            style={{ flex: '1 1 0', padding: '8px' }}
          >
            TB B
          </div>
        </div>
        {displaySets.map((set, index) => (
          <div
            key={set.numeroSet}
            className="d-flex align-items-center"
            style={{
              borderBottom: index < displaySets.length - 1 ? '1px solid #50555aff' : 'none',
              minHeight: '50px',
            }}
          >
            {/* Coluna 1: Número do Set */}
            <div
              className="fw-bold d-flex align-items-center justify-content-center flex-row col-1"
              style={{ minWidth: '50px', padding: '8px', borderRight: '1px solid #dee2e6' }}
            >
              {isSuperTiebreak(set.numeroSet) ? (
                <span style={{ color: '#dc3545' }}>ST</span>
              ) : (
                set.numeroSet
              )}
            </div>

            {/* Coluna 2: Games Equipe A */}
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ flex: '1 1 0', padding: '8px', borderRight: '1px solid #dee2e6' }}
            >
              {canEdit ? (
                <input
                  type="number"
                  min="0"
                  className={`form-control form-control-sm text-center ${isCampoInvalido(set.numeroSet, 'gamesA') ? 'is-invalid' : ''}`}
                  style={{ 
                    maxWidth: '80px', 
                    margin: '0 auto',
                    borderColor: isCampoInvalido(set.numeroSet, 'gamesA') ? '#dc3545' : undefined,
                    backgroundColor: isCampoInvalido(set.numeroSet, 'gamesA') ? '#fff5f5' : undefined,
                  }}
                  value={set.gamesA ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : Number(e.target.value);
                    onSetFieldUpdate(set.numeroSet, 'gamesA', value);
                  }}
                />
              ) : (
                <span>{set.gamesA ?? '-'}</span>
              )}
            </div>

            {/* Coluna 3: Tiebreak Equipe A */}
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ flex: '1 1 0', padding: '8px' }}
            >
              {canEdit ? (
                <input
                  type="number"
                  min="0"
                  disabled={!deveTerTiebreak(set)}
                  className={`form-control form-control-sm text-center ${isCampoInvalido(set.numeroSet, 'tiebreakA') ? 'is-invalid' : ''}`}
                  style={{ 
                    maxWidth: '80px',
                    borderColor: isCampoInvalido(set.numeroSet, 'tiebreakA') ? '#dc3545' : undefined,
                    backgroundColor: isCampoInvalido(set.numeroSet, 'tiebreakA') ? '#fff5f5' : (!deveTerTiebreak(set) ? '#e9ecef' : undefined),
                  }}
                  value={set.tiebreakA ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : Number(e.target.value);
                    onSetFieldUpdate(set.numeroSet, 'tiebreakA', value);
                  }}
                />
              ) : (
                <span className="small">{set.tiebreakA ? `TB: ${set.tiebreakA}` : '-'}</span>
              )}
            </div>

            {/* Coluna 4: Divisória Central */}
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: '50px',
                padding: '8px',
                borderLeft: '2px solid #000',
                borderRight: '2px solid #000',
                backgroundColor: '#f8f9fa',
              }}
            >
              <span className="fw-bold">x</span>
            </div>

            {/* Coluna 5: Games Equipe B */}
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ flex: '1 1 0', padding: '8px', borderRight: '1px solid #dee2e6' }}
            >
              {canEdit ? (
                <input
                  type="number"
                  min="0"
                  className={`form-control form-control-sm text-center ${isCampoInvalido(set.numeroSet, 'gamesB') ? 'is-invalid' : ''}`}
                  style={{ 
                    maxWidth: '80px', 
                    margin: '0 auto',
                    borderColor: isCampoInvalido(set.numeroSet, 'gamesB') ? '#dc3545' : undefined,
                    backgroundColor: isCampoInvalido(set.numeroSet, 'gamesB') ? '#fff5f5' : undefined,
                  }}
                  value={set.gamesB ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : Number(e.target.value);
                    onSetFieldUpdate(set.numeroSet, 'gamesB', value);
                  }}
                />
              ) : (
                <span>{set.gamesB ?? '-'}</span>
              )}
            </div>

            {/* Coluna 6: Tiebreak Equipe B */}
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ flex: '1 1 0', padding: '8px' }}
            >
              {canEdit ? (
                <input
                  type="number"
                  min="0"
                  disabled={!deveTerTiebreak(set)}
                  className={`form-control form-control-sm text-center ${isCampoInvalido(set.numeroSet, 'tiebreakB') ? 'is-invalid' : ''}`}
                  style={{ 
                    maxWidth: '80px',
                    borderColor: isCampoInvalido(set.numeroSet, 'tiebreakB') ? '#dc3545' : undefined,
                    backgroundColor: isCampoInvalido(set.numeroSet, 'tiebreakB') ? '#fff5f5' : (!deveTerTiebreak(set) ? '#e9ecef' : undefined),
                  }}
                  value={set.tiebreakB ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : Number(e.target.value);
                    onSetFieldUpdate(set.numeroSet, 'tiebreakB', value);
                  }}
                />
              ) : (
                <span className="small">{set.tiebreakB ? `TB: ${set.tiebreakB}` : '-'}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScoreboardGrid;

